import moment from 'moment';

/**
 * The message recorded against an e-warrant when an operator approves or rejects it is
 * three parts joined by this separator, in the order they are written:
 *
 *   2026-08-11T15:42:03Z - csp.operator - MSISDN is not provisioned on this network
 *   <utc timestamp>       <operator>      <what they typed>
 *
 * It travels to Xcipio as one string — it is stored as the ETSI
 * common:ApprovalDescription of the authorisation, which has nowhere else to put who
 * decided and when — so the parts are split back out again for display. The note itself
 * may contain the separator, so only the first two occurrences split.
 */
export const DECISION_PART_SEPARATOR = ' - ';

/**
 * The timestamp is written in UTC rather than the operator's own zone: it is read back by
 * whoever opens the e-warrant next, in whatever zone they are in, and only an absolute
 * instant survives that. It is turned back into local time for display.
 */
const UTC_TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

export type DecisionMessage = {
  /** The UTC instant the decision was recorded, or null when the message carries none. */
  decidedAt: string | null
  /** The operator who recorded it, or null when the message carries none. */
  decidedBy: string | null
  /** What the operator typed. The whole message when it is not one this UI wrote. */
  text: string
};

/** Whether a part of a message is the UTC timestamp this UI writes, and not free text. */
const isUtcTimestamp = (value: string): boolean =>
  value.endsWith('Z') && moment.utc(value, moment.ISO_8601, true).isValid();

/**
 * The message to record for a decision: when it was made and by whom, ahead of the note
 * the operator typed. A decision with no note is still stamped with both.
 */
export const buildDecisionMessage = (userName: string, note: string): string => {
  const parts = [moment.utc().format(UTC_TIMESTAMP_FORMAT), userName];
  const text = note.trim();

  return (text === '' ? parts : [...parts, text]).join(DECISION_PART_SEPARATOR);
};

/**
 * Splits a decision message back into its three parts.
 *
 * Anything that does not start with a UTC timestamp was not written by this UI — an
 * approval description that came in with the e-warrant, or a note recorded before the
 * decision was stamped — so it is handed back whole as the text, with nothing invented
 * for the two fields it does not carry.
 */
export const parseDecisionMessage = (message?: string | null): DecisionMessage => {
  const nothingRecorded: DecisionMessage = { decidedAt: null, decidedBy: null, text: '' };

  if (!message) {
    return nothingRecorded;
  }

  const firstSeparator = message.indexOf(DECISION_PART_SEPARATOR);

  if (firstSeparator < 0 || !isUtcTimestamp(message.slice(0, firstSeparator))) {
    return { ...nothingRecorded, text: message };
  }

  const decidedAt = message.slice(0, firstSeparator);
  const rest = message.slice(firstSeparator + DECISION_PART_SEPARATOR.length);
  const secondSeparator = rest.indexOf(DECISION_PART_SEPARATOR);

  const decidedBy = (secondSeparator < 0 ? rest : rest.slice(0, secondSeparator));
  const text = (secondSeparator < 0 ? '' : rest.slice(secondSeparator + DECISION_PART_SEPARATOR.length));

  return { decidedAt, decidedBy: (decidedBy === '' ? null : decidedBy), text };
};
